/**
 * Scene openings in exported books (Styling ▸ Paragraphs). A scene's first
 * paragraph gets `zw-flush-first` when the first-paragraph setting sets it
 * flush left. A chapter opening (the first paragraph of a chapter's first
 * scene, with an initial or lead-in on) gets `zw-chapter-opening`; its
 * small-caps lead-in (`zw-opening-lead`) and its initial
 * (`zw-opening-initial` with `zw-opening-drop` / `zw-opening-raised`, nested
 * in the lead-in when both are on) are wrapped in spans. PDF / EPUB style the
 * classes (`chapterParagraphCss`); DOCX reads them as run formatting.
 */

import { assertNever } from "../lib/assertNever";
import {
  initialLength,
  leadInLength,
  sceneFirstParagraph,
} from "../lib/chapterOpening";
import { isJust, just, nothing, type Maybe } from "../lib/maybe";
import type { ZwOpeningInitial, ZwParagraphFormatting } from "../lib/zwConfig";

/** Text nodes under `el`, in order. */
function textNodes(el: HTMLElement): Text[] {
  const walker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    nodes.push(node as Text);
  }
  return nodes;
}

/** Wrap characters [0, length) of `el`'s text in `span.className`, splitting
 * any formatting elements the range crosses. */
function wrapLeadingText(
  el: HTMLElement,
  length: number,
  className: string,
): Maybe<HTMLElement> {
  const nodes = textNodes(el);
  const first = nodes[0];
  if (typeof first === "undefined" || length <= 0) return nothing;
  let remaining = length;
  let end: { node: Text; offset: number } = { node: first, offset: 0 };
  for (const node of nodes) {
    const size = node.data.length;
    end = { node, offset: Math.min(size, remaining) };
    if (remaining <= size) break;
    remaining -= size;
  }
  const range = el.ownerDocument.createRange();
  range.setStart(el, 0);
  range.setEnd(end.node, end.offset);
  const span = el.ownerDocument.createElement("span");
  span.className = className;
  span.appendChild(range.extractContents());
  range.insertNode(span);
  return just(span);
}

/** How a scene's first paragraph is set: flush left or not, and the
 * chapter-opening initial / lead-in (`none` except a chapter's first scene). */
export type SceneOpening = Pick<
  ZwParagraphFormatting,
  "openingInitial" | "openingLeadIn"
> & { readonly flush: boolean };

export const PLAIN_SCENE_OPENING: SceneOpening = {
  flush: false,
  openingInitial: "none",
  openingLeadIn: "none",
};

/** The opening for a scene under `formatting`; `opensChapter` for a
 * chapter's first scene. */
export function sceneOpening(
  formatting: ZwParagraphFormatting,
  opensChapter: boolean,
): SceneOpening {
  const flush =
    sceneFirstParagraph(formatting.firstParagraph, opensChapter) !== "indented";
  return opensChapter
    ? {
        flush,
        openingInitial: formatting.openingInitial,
        openingLeadIn: formatting.openingLeadIn,
      }
    : { ...PLAIN_SCENE_OPENING, flush };
}

function initialClass(initial: ZwOpeningInitial): Maybe<string> {
  switch (initial) {
    case "none":
      return nothing;
    case "dropCap":
      return just("zw-opening-initial zw-opening-drop");
    case "raisedInitial":
      return just("zw-opening-initial zw-opening-raised");
    default:
      return assertNever(initial);
  }
}

/** Mark the first paragraph with text in `container`: flush, then as the
 * chapter opening with the lead-in span first and the initial inside it (or
 * the paragraph). */
export function markSceneOpening(
  container: HTMLElement,
  opening: SceneOpening,
): void {
  const initial = initialClass(opening.openingInitial);
  const leadIn = opening.openingLeadIn;
  const chapter = isJust(initial) || leadIn !== "none";
  if (opening.flush === false && chapter === false) return;
  const paragraph = Array.from(container.children).find(
    (child): child is HTMLElement =>
      child.tagName === "P" && (child.textContent ?? "").trim() !== "",
  );
  if (typeof paragraph === "undefined") return;
  if (opening.flush) paragraph.classList.add("zw-flush-first");
  if (chapter === false) return;
  paragraph.classList.add("zw-chapter-opening");
  const text = paragraph.textContent ?? "";
  let initialHost: HTMLElement = paragraph;
  if (leadIn !== "none") {
    const lead = wrapLeadingText(
      paragraph,
      leadInLength(text, leadIn),
      "zw-opening-lead",
    );
    if (isJust(lead)) initialHost = lead.value;
  }
  if (isJust(initial)) {
    wrapLeadingText(initialHost, initialLength(text), initial.value);
  }
}
